import { useState, useEffect, useRef } from 'react';
import { Avatar, Popover, Spin } from 'antd';
import * as Icon from 'react-bootstrap-icons';
import { ProfilePictureProps } from '../../../types/ProfileTypes';
import { deleteProfilePic, updateProfilePic } from '../../../api/hooks/useUserQuery';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { getServerErrorMsg } from '../../../helpers/errorHandler';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '../../../contexts/UserContext';
import { ActionMenu } from '../../../components/common/ActionMenu';
import defaultImage from '../../../assets/company/default-profile.png';
import ConfirmModelComponent, {
  ModelType,
} from '../../../components/model/ConfirmationModelComponent';

const ProfilePicture: React.FC<ProfilePictureProps> = ({ initialImage }) => {
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const updateProfilePicture = updateProfilePic();
  const { successNotification, errorNotification } = useNotificationContext();
  const { t } = useTranslation('profile');
  const { setProfileImageToContext } = useUserContext();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const deleteProfileImage = deleteProfilePic();

  useEffect(() => {
    if (initialImage) {
      setLoading(true);
      const img = new Image();
      img.src = initialImage;

      img.onload = () => {
        setProfileImage(initialImage);
        setLoading(false);
      };

      img.onerror = () => {
        console.error(t('imageLoadError'));
        setLoading(false);
      };
    } else {
      setLoading(false);
      setProfileImageToContext(defaultImage);
      setProfileImage(defaultImage);
    }
  }, [initialImage, t]);

  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const SUPPORTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

  const handleImageUpload = (info?: { file: any }) => {
    if (!info) {
      fileInputRef.current?.click();
      return;
    }

    const { file } = info;
    const fileToRead = file.originFileObj || file;

    if (!SUPPORTED_FILE_TYPES.includes(fileToRead.type)) {
      errorNotification(t('invalidFileTypeError'));
      console.error(t('invalidFileType'), fileToRead);
      return;
    }

    if (fileToRead.size > MAX_FILE_SIZE) {
      errorNotification(t('fileTooLargeError'));
      console.error(t('fileTooLarge'), fileToRead);
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const base64Image = reader.result as string;
      const trimmedImage = base64Image.replace(/^data:image\/[a-zA-Z0-9+]+;base64,/, '');
      setLoading(true);
      setProfileImage('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      updateProfilePicture.mutate(trimmedImage, {
        onSuccess: () => {
          successNotification(t('profilePictureUpdatedSuccessfully'));
          setProfileImage(base64Image);
          setLoading(false);
          setProfileImageToContext(base64Image);
        },
        onError: (error: any) => {
          const errorMsg = getServerErrorMsg(error);
          errorNotification(errorMsg);
          console.error(errorMsg);
          setLoading(false);
        },
      });
    };
    reader.readAsDataURL(fileToRead);
  };

  const handleDeleteProfileImage = () => {
    setIsDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    deleteProfileImage.mutate(
      {},
      {
        onSuccess: (data: any) => {
          if (data.data.statusCode == 200) {
            successNotification(t('profilePictureDeletedSuccessfully'));
            setLoading(false);
            setProfileImageToContext(defaultImage);
            setProfileImage(defaultImage);
            if (fileInputRef.current) fileInputRef.current.value = '';
          } else if (data.data.statusCode == 400) {
            errorNotification(t('profilePictureAlreadyDeleted'));
          }
        },
        onError: (error: any) => {
          const errorMsg = getServerErrorMsg(error);
          errorNotification(errorMsg);
          console.error(errorMsg);
          setLoading(false);
        },
      }
    );
    setIsDeleteModalVisible(false);
  };

  const cancelDelete = () => {
    setIsDeleteModalVisible(false);
  };

  type MenuItem = {
    text: string;
    icon: JSX.Element;
    click: () => void;
    description?: string;
    iconColor?: string;
  };

  const menuItems: MenuItem[] = [
    {
      text: t('updateImage'),
      icon: <Icon.Pencil />,
      click: () => handleImageUpload(),
      description: t('supportedFileTypesInfo'),
    },
  ];

  if (profileImage && profileImage !== defaultImage) {
    menuItems.push({
      text: t('deleteImage'),
      icon: <Icon.Trash />,
      click: () => handleDeleteProfileImage(),
      iconColor: 'red',
    });
  }

  return (
    <div className="relative aspect-square w-full max-w-[200px] rounded-full border-2 border-primary p-1">
      {loading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-50">
          <Spin size="large" />
        </div>
      ) : (
        <Avatar
          size={200}
          src={profileImage || undefined}
          alt="Profile"
          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
        />
      )}

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={(e) => handleImageUpload({ file: e.target.files?.[0] })}
      />
      <Popover
        content={<ActionMenu menuItems={menuItems} />}
        className="cursor-pointer"
        placement="bottom"
        trigger="hover"
        overlayStyle={{ zIndex: 800 }}
      >
        <div
          className="absolute bottom-2 right-2 flex cursor-pointer items-center justify-center rounded-full bg-primary p-2 shadow-md bg-white"
          style={{
            backgroundColor: 'white',
            borderRadius: '50%',
            top: '80%',
            left: '80%',
            transform: 'translate(-25%, -25%)',
          }}
        >
          <Icon.CameraFill size={24} className='text-primary' />
        </div>
      </Popover>
      <ConfirmModelComponent
        isOpen={isDeleteModalVisible}
        onClickedCancel={cancelDelete}
        header={t('confirmDeleteTitle')}
        content={<p>{t('confirmDeleteMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: confirmDelete,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelDelete,
          },
        }}
        modelType={ModelType.DANGER}
      />
    </div>
  );
};

export default ProfilePicture;
